/** Stream music using overlapping media elements, never a shared WebAudio graph. */
export class CrossfadeMusic {
  current: HTMLAudioElement | null = null;
  private outgoing: HTMLAudioElement | null = null;
  private prepared: HTMLAudioElement | null = null;
  private gains = new Map<HTMLAudioElement, number>();
  private url: string | null = null;
  private looping = false;
  private generation = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private fadeStart: number | null = null;
  private fadeLength = 5;
  private outgoingGain = 1;
  private volume = 1;
  private overlapDenied = false;

  constructor(private overlap = 5, private onEnded: () => void = () => {}) {}

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    for (const [audio, gain] of this.gains) audio.volume = this.volume * gain;
  }

  async play(url: string, looping: boolean, allowed: boolean): Promise<void> {
    this.looping = looping;
    if (this.url === url && this.current) {
      if (!looping && this.prepared) { this.dispose(this.prepared); this.prepared = null; }
      if (allowed) await this.resume();
      return;
    }
    ++this.generation;
    this.overlapDenied = false;
    if (this.prepared) this.dispose(this.prepared);
    this.prepared = null;
    if (this.outgoing) this.dispose(this.outgoing);
    this.outgoing = null;
    this.url = url;
    this.replace(this.makeAudio(url), this.overlap);
    if (allowed) await this.resume();
  }

  async resume(): Promise<void> {
    const audio = this.current;
    if (!audio || !audio.paused) return;
    try { await audio.play(); }
    catch (error) {
      // The next real gesture retries this element; map loading is independent.
      if (this.current === audio && this.outgoing) {
        this.dispose(this.outgoing); this.outgoing = null;
        this.fadeStart = null; this.gains.set(audio, 1); this.setVolume(this.volume);
      }
      throw error;
    }
  }

  private makeAudio(url: string): HTMLAudioElement {
    const audio = new Audio(url);
    audio.preload = 'auto';
    audio.loop = false;
    audio.volume = 0;
    this.gains.set(audio, 0);
    const generation = this.generation;
    audio.addEventListener('playing', () => {
      if (generation !== this.generation || this.current !== audio) return;
      if (this.outgoing && this.fadeStart === null) this.fadeStart = audio.currentTime;
      this.advance();
    });
    audio.addEventListener('timeupdate', () => this.advance());
    audio.addEventListener('ended', () => {
      if (generation !== this.generation || this.current !== audio) return;
      if (this.looping) this.nextLoop();
      else this.onEnded();
    });
    return audio;
  }

  private replace(audio: HTMLAudioElement, seconds: number): void {
    const old = this.current;
    this.outgoing = old && !old.paused && !old.ended ? old : null;
    if (old && !this.outgoing) this.dispose(old);
    this.current = audio;
    this.fadeLength = Math.max(0.05, seconds);
    this.fadeStart = null;
    this.outgoingGain = this.outgoing ? (this.gains.get(this.outgoing) ?? 1) : 1;
    this.gains.set(audio, this.outgoing ? 0 : 1);
    this.setVolume(this.volume);
    if (this.timer === null) this.timer = setInterval(() => this.advance(), 100);
  }

  private nextLoop(): void {
    const old = this.current;
    if (!old || !this.url || this.outgoing || this.overlapDenied || old.paused && !old.ended) return;
    const audio = this.prepared ?? this.makeAudio(this.url);
    this.prepared = null;
    const seconds = Number.isFinite(old.duration) ? Math.min(this.overlap, old.duration / 2) : this.overlap;
    this.replace(audio, seconds);
    const generation = this.generation;
    void audio.play().catch(error => {
      if (this.generation !== generation || this.current !== audio) return;
      this.dispose(audio); this.current = old; this.outgoing = null; this.fadeStart = null;
      this.gains.set(old, 1); this.setVolume(this.volume);
      // Keep sound available if a mobile browser denies a second media element.
      old.loop = true;
      this.overlapDenied = true;
      if (old.ended) void old.play().catch(() => {});
      console.warn('Music overlap deferred; continuing the current track.', error);
    });
  }

  /** Fades follow audio playback time, so buffering/background pauses cannot skip them. */
  private advance(): void {
    const audio = this.current;
    if (!audio || audio.paused) return;
    if (this.outgoing && this.fadeStart !== null) {
      const fraction = Math.min(1, Math.max(0, (audio.currentTime - this.fadeStart) / this.fadeLength));
      this.gains.set(audio, Math.sin(fraction * Math.PI / 2));
      this.gains.set(this.outgoing, this.outgoingGain * Math.cos(fraction * Math.PI / 2));
      this.setVolume(this.volume);
      if (fraction === 1) {
        this.dispose(this.outgoing); this.outgoing = null; this.fadeStart = null;
        this.gains.set(audio, 1); this.setVolume(this.volume);
      }
    }
    if (!this.looping || this.overlapDenied || this.outgoing || !Number.isFinite(audio.duration) || audio.duration <= 0) return;
    const remaining = audio.duration - audio.currentTime;
    if (!this.prepared && remaining <= Math.max(20, this.overlap * 2)) this.prepared = this.makeAudio(this.url!);
    if (remaining <= Math.min(this.overlap, audio.duration / 2)) this.nextLoop();
  }

  private dispose(audio: HTMLAudioElement): void {
    this.gains.delete(audio);
    audio.pause(); audio.removeAttribute('src'); audio.load();
  }

  stop(): void {
    ++this.generation;
    for (const audio of [...this.gains.keys()]) this.dispose(audio);
    this.current = this.outgoing = this.prepared = null;
    this.url = null; this.fadeStart = null;
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
  }
}
